<template>
  <Swiper
    v-if="!withThumbnails"
    slides-per-view="1"
    :modules="modules"
    space-between="0.5rem"
    :free-mode="false"
    class="collection-carousel"
    wrapper-tag="ul"
    :navigation="navigationOptions"
    :pagination="paginationOptions"
    :a11y="{ slideRole: '' }"
    @swiper="setMainSwiper"
    @slide-change="handleSlideChange"
  >
    <SwiperSlide
      v-for="(item, index) in items.filter(i => i)"
      :key="item._uid"
      tag="li"
      @click="tracking(item)"
    >
      <StoryblokComponent
        :blok="item"
        :stop-video="initState.value"
        :class="childClasses"
        :context="context"
        :level="childLevel"
        :is-centered="childCentered"
        :text-size="childTextSize"
        :title-size="childTitleSize"
        :size-hint="sizeHint"
        :size-hint-mobile="sizeHintMobile"
        :quality="slideQuality(index)"
        :fetch-priority="slideFetchPriority(index)"
        :loading="slideLoading(index)"
        :is-guide="isGuide"
        :is-editorial="isEditorial"
        :is-file="isFile"
        :style="item.background_color
          ? {
              '--parent-bg': `${item.background_color} !important`,
              background: `${item.background_color} !important`,
            }
          : {}"
      />
    </SwiperSlide>
  </Swiper>

  <div v-else class="carousel-with-thumbnails">
    <swiper
      :slides-per-view="1"
      :initial-slide="clickedSlide"
      class="collection-carousel"
      space-between="20%"
      wrapper-tag="ul"
      :navigation="navigationOptions"
      :modules="modules"
      :thumbs="{ swiper: thumbsSwiper }"
      :pagination="paginationOptions"
      @swiper="setMainSwiper"
      @slide-change="handleSlideChange"
    >
      <swiper-slide v-for="(item, index) in items" :key="item._uid" tag="li">
        <StoryblokComponent
          :blok="item"
          :stop-video="initState.value"
          :size-hint="sizeHint"
          :size-hint-mobile="sizeHintMobile"
          :quality="slideQuality(index)"
          :fetch-priority="slideFetchPriority(index)"
          :loading="slideLoading(index)"
        />
      </swiper-slide>
    </swiper>

    <swiper
      :modules="[Thumbs]"
      watch-slides-progress
      :slides-per-view="7"
      space-between="8"
      wrapper-tag="ul"
      :free-mode="true"
      :class="`collection-carousel-thumbs${
        items?.length < 7 ? ' centered' : ''
      }`"
      @swiper="setThumbsSwiper"
    >
      <swiper-slide v-for="(item, index) in items" :key="item._uid" tag="li">
        <StoryblokComponent
          :blok="item"
          :play-disabled="true"
          fetch-priority="low"
          loading="lazy"
          :max-width="320"
        />
      </swiper-slide>
    </swiper>
  </div>
</template>

<script setup>
import { Navigation, Pagination, A11y, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/a11y";
import "swiper/css/thumbs";

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  withThumbnails: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  clickedSlide: {
    type: Number,
    required: false,
    default: () => 0,
  },
  childClasses: {
    type: String,
    required: false,
    default: "",
  },
  context: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  childLevel: {
    type: Number,
    required: false,
    default: () => 1,
  },
  childCentered: {
    type: Boolean,
    required: false,
    default: () => true,
  },
  childTextSize: {
    type: String,
    required: false,
    default: "size-xs",
  },
  childTitleSize: {
    type: String,
    required: false,
    default: "size-m",
  },
  sizeHint: {
    type: Number,
    required: false,
    default: 100,
  },
  sizeHintMobile: {
    type: Number,
    required: false,
    default: undefined,
  },
  isFile: {
    type: Boolean,
    required: false,
    default: false,
  },
  isEditorial: {
    type: Boolean,
    required: false,
    default: false,
  },
  isGuide: {
    type: Boolean,
    required: false,
    default: false,
  },
  prioritizeFirstMedia: {
    type: Boolean,
    required: false,
    default: false,
  },
  // Must match the SSR LCP AssetImage quality when prioritizeFirstMedia is set,
  // otherwise hydration swaps to a different Storyblok URL and LCP restarts.
  quality: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const slideFetchPriority = (index) =>
  props.prioritizeFirstMedia && index === 0 ? "high" : "low";

const slideLoading = (index) =>
  props.prioritizeFirstMedia && index === 0 ? "eager" : "lazy";

const slideQuality = (index) =>
  props.prioritizeFirstMedia && index === 0 ? props.quality : undefined;

const trackingStore = useTrackingStore();
const modules = computed(() => {
  return [Navigation, Pagination, A11y, Thumbs];
});

const initState = reactive({ value: false });

const activeIndex = ref(0);
const swiperRef = ref(null);

const paginationOptions = computed(() => ({
  clickable: true,
  renderBullet: (index, className) => {
    const current = swiperRef.value?.realIndex ?? 0;
    const distance = Math.abs(index - current);
    const ariaLabel = `Go to slide ${index + 1}`;

    if (distance > 2) {
      return `
        <span
          class="${className} hidden-bullet"
          role="button"
          tabindex="0"
          aria-label="${ariaLabel}"
        ></span>
      `;
    }

    return `
      <span
        class="${className} bullet-distance-${distance}"
        role="button"
        tabindex="0"
        aria-label="${ariaLabel}"
      ></span>
    `;
  },
}));

const navigationOptions = {
  enabled: true,
  addIcons: false,
};

const emit = defineEmits(["slide-change", "ready"]);

const setMainSwiper = (swiper) => {
  swiperRef.value = swiper;
  activeIndex.value = swiper.activeIndex;
  emit("ready", swiper);
};

onMounted(() => {
  const nextButton = document.querySelector(".swiper-button-next");
  const prevButton = document.querySelector(".swiper-button-prev");

  if (nextButton) {
    nextButton.setAttribute("aria-label", useMicroCopy("slide.button.next"));
  }

  if (prevButton) {
    prevButton.setAttribute("aria-label", useMicroCopy("slide.button.previous"));
  }
});

const handleSlideChange = (swiper) => {
  activeIndex.value = swiper.activeIndex;
  emit("slide-change", swiper.activeIndex);

  if (swiper.pagination) {
    swiper.pagination.render();
    swiper.pagination.update();
  }
};

const tracking = (data) => {
  if ("isPrivate" in data) {
    trackingStore.sendTrackingData({
      event: "click.action",
      click: useSnakeCaseFilter(data.cta_label),
      click_chapter1: "facilities_recommendation",
      click_chapter2: "CTA",
      click_chapter3: useSnakeCaseFilter(getTextFromRichText(data.title)),
    });
    return;
  } else {
    trackingStore.sendTrackingData({
      event: "click.action",
      click: useSnakeCaseFilter(data.cta_label),
      click_chapter1: "push_cards",
      click_chapter2: "CTA",
      click_chapter3: useSnakeCaseFilter(getTextFromRichText(data.title)),
    });
    return;
  }
  if (props.childClasses === "news-card") {
    trackingStore.sendTrackingData({
      event: "click.navigation",
      click: "learn_more",
      click_chapter1: "news",
      click_chapter2: "carrousel",
      click_chapter3: useSnakeCaseFilter(getTextFromRichText(data?.title)),
    });
  }
  if (props.childClasses === "card") {
    trackingStore.sendTrackingData({
      event: "click.navigation",
      click: useSnakeCaseFilter(data?.cta_label),
      click_chapter1: "push_cards",
      click_chapter2: "CTA",
      click_chapter3: useSnakeCaseFilter(getTextFromRichText(data?.title)),
    });
  }
};

const thumbsSwiper = ref(null);
const setThumbsSwiper = (swiper) => {
  thumbsSwiper.value = swiper;
};
</script>

<style lang="scss">
@mixin swiper-button {
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;

  &:after {
    content: "";
    display: block;
    font-size: 0;
    width: 3.25rem;
    height: 3.25rem;
    padding: 0;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: 1.5rem 1.5rem;
  }
}

// Zone cliquable 24×24px ; rendu visuel inchangé sur ::after
@mixin swiper-pagination-bullet-touch-area {
  position: relative;
  background: transparent !important;
  opacity: 1 !important;
  border-radius: 0 !important;

  &:not(.swiper-pagination-bullet-active) {
    width: 1.5rem !important;
    height: 1.5rem !important;
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: var(--swiper-pagination-bullet-size, var(--swiper-pagination-bullet-width, 8px));
    height: var(--swiper-pagination-bullet-size, var(--swiper-pagination-bullet-height, 8px));
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: transform 0.25s ease, opacity 0.25s ease;
    pointer-events: none;
  }
}

.collection-carousel {
  border-radius: $radius-l;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .swiper-wrapper {
    margin: 0;
    list-style: none;
    padding: 0;
    border-radius: $radius-l;
  }

  img {
    object-fit: cover;
    display: block;
    width: 100%;
    aspect-ratio: 1.33 !important;
  }

  .media-text-cta .media {
    border-radius: $radius-s;
  }

  .youtube-player {
    aspect-ratio: 1.33 !important;
  }

  .swiper-button-prev,
  .swiper-button-next {
    @include swiper-button;
    background-color: var(--solid-07);
  }

  .swiper-button-prev:after {
    background-image: url("../assets/svg/func_left.svg");
  }

  .swiper-button-next:after {
    background-image: url("../assets/svg/func_right.svg");
  }

  .swiper-pagination {
    width: fit-content;
    position: relative;
    margin: -1.375rem auto 0 auto;
    left: auto;
  }

  .swiper-pagination-bullet {
    @include swiper-pagination-bullet-touch-area;
    transition: none;

    &::after {
      background-color: var(--solid-07);
      opacity: 0.75;
    }
  }

  .swiper-pagination-bullet-active {
    width: 3.2rem !important;
    height: 1.5rem !important;
  }

  .swiper-pagination-bullet-active::after {
    width: 2rem;
    height: var(--swiper-pagination-bullet-size, var(--swiper-pagination-bullet-height, 8px));
    border-radius: 0.25rem;
    opacity: 1;
  }

  .bullet-distance-0::after {
    transform: translate(-50%, -50%) scale(1);
  }

  .bullet-distance-1::after {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.9;
  }

  .bullet-distance-2::after {
    transform: translate(-50%, -50%) scale(0.6);
    opacity: 0.7;
  }

  // Distant bullets
  .hidden-bullet {
    display: none !important;
  }
}

.carousel-with-thumbnails {
  grid-column: 3 / -3;
  grid-row: 2 / 2;

  &:before {
    background: var(--solid-07);
    position: absolute;
    @include inset(0, 10vw);
    content: "";
    box-shadow: var(--solid-07) 0 0 0 0.5rem;
    border-radius: $radius-l;
  }

  .collection-carousel {
    padding: 0 10vw;

    .swiper-button-prev,
    .swiper-button-next {
      @include swiper-button;
      @include gradientAlt;
      background-color: transparent;

      &::before {
        content: "";
        width: 3rem;
        height: 3rem;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
        background-color: var(--solid-03);
      }

      &:after {
        z-index: 12;
        background-color: transparent;
      }
    }
  }

  .swiper-pagination {
    height: auto;
    display: flex;
    align-items: center;
    top: auto !important;
    bottom: auto !important;
    margin-top: -0.5rem;
    transform: translateY(-1rem);
    justify-content: center;
  }

  .swiper-pagination-bullet-active::after {
    width: 3.2rem !important;
    height: 0.5rem;
    border-radius: 0.25rem;
  }

  .collection-carousel-thumbs {
    width: 59.5vw;
    margin-top: 0.5rem;
    border-radius: $radius-l;
    overflow: hidden;

    &.centered .swiper-wrapper {
      display: flex;
      justify-content: center;
    }

    .swiper-slide {
      opacity: 0.6;
    }

    .swiper-slide-thumb-active {
      opacity: 1;
    }

    img {
      border-radius: $radius-l;
      object-fit: cover;
      display: block;
      cursor: pointer;
      aspect-ratio: 1.33;
    }

    .youtube-player {
      aspect-ratio: 1.33 !important;
    }
  }
}

.carousel-navigation-bottom {
  padding-bottom: 5.75rem !important;
  border-radius: 0;

  &.lock {
    padding-bottom: 0 !important;
  }

  > .swiper-button-prev,
  > .swiper-button-next {
    @include swiper-button;
    @include gradientAlt;

    bottom: 0;
    top: auto;
    left: auto;
    z-index: 11;

    &::before {
      content: "";
      width: 3rem;
      height: 3rem;
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate3d(-50%, -50%, 0);
      background-color: var(--solid-07);
    }

    &:after {
      z-index: 12;
    }
  }

  > .swiper-button-prev {
    right: calc(3.25rem + 0.75rem + 1.12rem);

    @include for-tablet-portrait-up {
      right: calc(3.25rem + 0.75rem + 2.5rem);
    }

    @include for-desktop-up {
      right: calc(3.25rem + 0.75rem + 3.75rem);
    }

    &:after {
      background-image: url("../assets/svg/func_left.svg");
    }
  }

  > .swiper-button-next {
    right: 1.12rem;

    @include for-tablet-portrait-up {
      right: 2.5rem;
    }

    @include for-desktop-up {
      right: 3.75rem;
    }

    &:after {
      background-image: url("../assets/svg/func_right.svg");
    }
  }

  > .swiper-pagination {
    height: 3.25rem;
    display: flex;
    align-items: center;
    top: auto !important;
    bottom: 0 !important;
    left: 0;
    width: 100%;
    position: absolute;

    @include for-tablet-portrait-up {
      left: 0;
      padding-right: col-span();
    }

    @include for-desktop-up {
      justify-content: center;
    }

    .swiper-pagination-bullet {
      @include swiper-pagination-bullet-touch-area;

      &::after {
        width: 0.5rem;
        height: 0.5rem;
        background: var(--text-50);
        opacity: 1;
      }
    }

    .swiper-pagination-bullet-active {
      width: 3.2rem !important;
      height: 1.5rem !important;
    }

    .swiper-pagination-bullet-active::after {
      @include gradientAlt;
      width: 3.2rem !important;
      height: 0.5rem;
      border-radius: 0.25rem;
    }
  }
}

// Design for carousel-full-width
.carousel-full-width {
  @include for-tablet-landscape-up {
    margin-left: 0 !important;
    margin-right: 0 !important;
    overflow: visible !important;

    .swiper {
      overflow: visible !important;
    }
    .swiper-button-prev {
      right: 4rem;
    }
    .swiper-button-next {
      right: 0;
    }
  }
}


// CSS for edge case caused by CollectionNews and others parent components
.swiper-pagination:has(.bullet-distance-1.swiper-pagination-bullet-active)
  .bullet-distance-2 {
    display: none;
  }

.swiper-pagination:has(.bullet-distance-1.swiper-pagination-bullet-active)
  .swiper-pagination-bullet.bullet-distance-1:not(.swiper-pagination-bullet-active)::after {
    transform: translate(-50%, -50%) scale(0.6);
  }

.swiper-pagination:has(.bullet-distance-1.swiper-pagination-bullet-active)
  .bullet-distance-0::after {
    transform: translate(-50%, -50%) scale(0.8);
  }
</style>